// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the time picker for a mail that asks to agree a meeting time (design/draft-reply-slots A).
// One row of chips per day with the times worth offering for the chosen length: free for everyone
// whose calendar could be read, or (dashed) a few where someone is busy. Under the chips, who is
// free at the picked (or hovered) time, and on the Konvo card a switch for the Teams invite.
// Used on the Konvo card, in its phone sheet and in the composer's When line.

import styles from '@app/features/konvo_mail/components/MailMeetingPicker.module.css';
import * as M from '@app/features/konvo_mail/components/MailMeetingStrings';
import {
	chipTimeText,
	dayLabel,
	durationText,
	type MailMeetingAction,
	type MeetingChoice,
	optionsByDay,
	optionsFor,
} from '@app/features/konvo_mail/state/MailMeeting';
import {peopleLine} from '@app/features/konvo_mail/utils/MailMeetingText';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {CalendarPlusIcon} from '@app/features/ui/icons/Icons';
import {Segmented} from '@app/features/ui/konvo_controls/KonvoControls';
import {getCurrentLocale} from '@app/features/user/utils/LocaleUtils';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useState} from 'react';

interface MailMeetingPickerProps {
	action: MailMeetingAction;
	/** The picked length and time; `selected` overrides which chip shows as picked (the fallback picker). */
	choice: MeetingChoice;
	selected?: string | null;
	onPick: (start: string) => void;
	onDuration: (minutes: number) => void;
	/** The invite switch (the Konvo card); the composer has its own toggle on the When line. */
	onInvite?: (on: boolean) => void;
	heading?: string;
	phone?: boolean;
}

function initials(name: string): string {
	const parts = name.trim().split(/\s+/);
	return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

export const MailMeetingPicker = observer(
	({action, choice, selected, onPick, onDuration, onInvite, heading, phone}: MailMeetingPickerProps) => {
		const {i18n} = useLingui();
		const locale = getCurrentLocale();
		const [hovered, setHovered] = useState<string | null>(null);
		const options = optionsFor(action, choice.duration);
		const picked = selected === undefined ? choice.start : selected;
		const shown =
			options.find((option) => option.start === hovered) ?? options.find((option) => option.start === picked) ?? null;
		const days = optionsByDay(options);
		return (
			<div className={clsx(styles.box, phone && styles.phone)} data-flx="konvo-mail.meeting.picker">
				<div className={styles.head}>
					<span className={styles.label}>{heading ?? i18n._(M.PICK_TIME_DESCRIPTOR)}</span>
					<Segmented
						compact
						aria-label={i18n._(M.LENGTH_DESCRIPTOR)}
						className={styles.lengths}
						value={String(choice.duration)}
						onChange={(value) => onDuration(Number(value))}
						options={action.durations.map((minutes) => ({
							value: String(minutes),
							children: durationText(minutes, locale),
							'data-flx': `konvo-mail.meeting.length-${minutes}`,
						}))}
					/>
				</div>
				{days.length ? (
					days.map((day) => (
						<div key={day.day} className={styles.day}>
							<span className={styles.dayName}>{dayLabel(day.options[0]!.start, locale)}</span>
							<div className={styles.chips}>
								{day.options.map((option) => {
									const on = option.start === picked;
									return (
										<button
											key={option.start}
											type="button"
											className={clsx(styles.chip, on && styles.on, option.busy.length > 0 && styles.part)}
											aria-pressed={on}
											title={option.busy.length ? peopleLine(i18n, action, option, locale) : undefined}
											onClick={() => onPick(option.start)}
											onMouseEnter={() => setHovered(option.start)}
											onMouseLeave={() => setHovered((current) => (current === option.start ? null : current))}
											onFocus={() => setHovered(option.start)}
											onBlur={() => setHovered((current) => (current === option.start ? null : current))}
											data-flx="konvo-mail.meeting.chip"
										>
											{chipTimeText(option.start, locale)}
										</button>
									);
								})}
							</div>
						</div>
					))
				) : (
					<p className={styles.empty}>{i18n._(M.NO_TIME_DESCRIPTOR)}</p>
				)}
				{action.people.length ? (
					<div className={styles.people} data-flx="konvo-mail.meeting.people">
						<span className={styles.avatars} aria-hidden="true">
							{action.people.map((person, index) => {
								const status = !person.known
									? styles.unknown
									: shown?.busy.includes(index)
										? styles.busy
										: shown
											? styles.free
											: undefined;
								return (
									<span key={person.address} className={clsx(styles.avatar, status)}>
										{person.initials || initials(person.name)}
									</span>
								);
							})}
						</span>
						<span className={styles.peopleText}>{peopleLine(i18n, action, shown, locale)}</span>
					</div>
				) : null}
				{onInvite ? (
					<Switch
						compact
						className={styles.invite}
						icon={undefined}
						label={
							<span className={styles.inviteLabel}>
								<CalendarPlusIcon size={16} />
								{i18n._(M.INVITE_SWITCH_DESCRIPTOR)}
							</span>
						}
						value={choice.invite}
						disabled={!choice.start}
						onChange={onInvite}
					/>
				) : null}
			</div>
		);
	},
);
